// ==========================================
// FILE: \src\screens\LoginScreen.js
// ==========================================

import React, { useState, useEffect } from 'react';
import { 
  View, Text, TextInput, TouchableOpacity, StyleSheet, 
  KeyboardAvoidingView, Platform, ActivityIndicator, Modal, ScrollView, Linking, Keyboard
} from 'react-native';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  sendPasswordResetEmail,
  sendEmailVerification,
  signOut,
  signInWithCredential,
  GoogleAuthProvider,
  OAuthProvider,
  linkWithCredential,
  signInWithPopup,
  getAuth,
  EmailAuthProvider,
  updatePassword
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import * as LocalAuthentication from 'expo-local-authentication';
import * as SecureStore from 'expo-secure-store';
import Constants from 'expo-constants';
import { 
  Eye, EyeOff, Fingerprint, CheckCircle, AlertCircle, 
  User, Globe, ChevronRight, CheckSquare, Square, Info, ShieldAlert, KeyRound, Lock, MailCheck
} from 'lucide-react-native';

// Importazione moduli di autenticazione nativa condizionale e bottoni ufficiali
import { GoogleSignin, GoogleSigninButton, statusCodes } from '@react-native-google-signin/google-signin';
import * as AppleAuthentication from 'expo-apple-authentication';

// Importiamo il gancio del contesto per usufruire del motore di traduzione globale
import { useWineContext } from '../context/WineContext';

const BASE_WEB_URL = 'https://vinologic.it';
const GOOGLE_WEB_CLIENT_ID = '1016731205324-bamie68gtgts8ukouaka1fbudssnghu2.apps.googleusercontent.com';

export default function LoginScreen() {
  const { t, refreshWines } = useWineContext();

  // Modalità del form e-mail tradizionale: true = Registrazione, false = Login
  const [isEmailSignupMode, setIsEmailSignupMode] = useState(false);

  // Campi Form Onboarding / Registrazione
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('it');
  const [country, setCountry] = useState('Italia'); 
  const [isAdultConfirmed, setIsAdultConfirmed] = useState(false); 
  const [isTermsAccepted, setIsTermsAccepted] = useState(false); 
  
  // Campi per Sicurezza Account Social in Onboarding
  const [socialSecurePassword, setSocialSecurePassword] = useState('');
  const [showSocialPassword, setShowSocialPassword] = useState(false);
  const [isBiometricRequested, setIsBiometricRequested] = useState(false);
  const [isSocialRegistration, setIsSocialRegistration] = useState(false);

  // Campi Comuni
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isBiometricSupported, setIsBiometricSupported] = useState(false);

  // Stati Popup Interattivo
  const [customAlert, setCustomAlert] = useState({ 
    visible: false, 
    title: '', 
    message: '', 
    type: 'info', 
    onConfirm: null, 
    confirmText: 'OK', 
    onCancel: null, 
    cancelText: 'Annulla' 
  });

  const [showAccountSelector, setShowAccountSelector] = useState(false);
  const [savedAccountsList, setSavedAccountsList] = useState([]);

  // Stati dedicati alla logica di Account Linking
  const [isLinkingMode, setIsLinkingMode] = useState(false);
  const [pendingCredential, setPendingCredential] = useState(null);
  const [linkPassword, setLinkPassword] = useState('');

  // Stato per l'Onboarding Legale post-autenticazione
  const [onboardingUser, setOnboardingUser] = useState(null);

  useEffect(() => {
    (async () => {
      const compatible = await LocalAuthentication.hasHardwareAsync();
      const enrolled = await LocalAuthentication.isEnrolledAsync();
      setIsBiometricSupported(compatible && enrolled);
    })();

    if (Platform.OS !== 'web') {
      GoogleSignin.configure({
        webClientId: GOOGLE_WEB_CLIENT_ID,
        offlineAccess: true,
        scopes: ['profile', 'email'],
      });
    }
  }, []);

  const showAppAlert = (
    title, 
    message, 
    type = 'info', 
    onConfirm = null, 
    confirmText = 'OK', 
    onCancel = null, 
    cancelText = 'Annulla'
  ) => {
    Keyboard.dismiss();
    setCustomAlert({ 
      visible: true, 
      title, 
      message, 
      type, 
      onConfirm, 
      confirmText, 
      onCancel, 
      cancelText 
    });
  };

  const closeAppAlert = () => {
    setCustomAlert(prev => ({ ...prev, visible: false }));
  };

  const showAgeInfo = () => {
    showAppAlert(
      t('login.alerts.ageInfoTitle', language),
      t('login.alerts.ageInfoMsg', language),
      "info"
    );
  };

  const openLegalDocument = (type) => {
    const url = type === 'privacy' 
      ? `${BASE_WEB_URL}/${language}/privacy.html` 
      : `${BASE_WEB_URL}/${language}/terms.html`;
      
    Linking.openURL(url).catch(() => 
      showAppAlert(
        t('login.alerts.missingCredentialsTitle', language), 
        t('login.alerts.linkErrorMsg', language), 
        "error"
      )
    );
  };

  // --- LOGICA GESTIONE ERRORI ED ACCOUNT LINKING ---
  const handleAuthErrors = (error, sourceContext = "Generico") => {
    console.error(`[VinoLogic DIAGNOSTIC] Errore in [${sourceContext}]:`, error.code, error.message);
    
    if (error.code === 'auth/account-exists-with-different-credential') {
      setIsLinkingMode(true);
      
      const credential = 
        GoogleAuthProvider.credentialFromError(error) || 
        OAuthProvider.credentialFromError(error) || 
        error.customCredential;

      setPendingCredential(credential);
      
      showAppAlert(
        "Cantina Esistente", 
        "Esiste già una cantina registrata con questo indirizzo e-mail. Inserisci la tua password attuale per unire gli accessi e non perdere i tuoi vini.", 
        "info"
      );
    } else if (error.code === 'auth/email-already-in-use') {
      showAppAlert(t('login.alerts.missingCredentialsTitle', language), t('login.alerts.emailInUseMsg', language), 'error');
    } else if (error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found' || error.code === 'auth/invalid-credential') {
      showAppAlert(t('login.alerts.loginErrorTitle', language), t('login.alerts.loginErrorMsg', language), 'error');
    } else if (error.code === 'auth/too-many-requests') {
      showAppAlert("Troppi Tentativi", "L'accesso a questo account è stato temporaneamente bloccato per troppi tentativi falliti. Riprova più tardi o reimposta la password.", 'error');
    } else {
      showAppAlert(
        "Diagnostica", 
        `Codice: ${error.code}\n\n${error.message}`, 
        'error'
      );
    }
  };

  const executeAccountLinking = async () => {
    if (!linkPassword || !pendingCredential) {
      showAppAlert("Errore", "Credenziali mancanti per l'unione dei profili.", "error");
      return;
    }
    if (Platform.OS === 'web') document.body.style.cursor = 'wait';
    setLoading(true);
    try {
      const activeAuth = Platform.OS === 'web' ? getAuth() : auth;
      const userCredential = await signInWithEmailAndPassword(activeAuth, email.trim() || activeAuth.currentUser?.email, linkPassword);
      await linkWithCredential(userCredential.user, pendingCredential);
      showAppAlert("Accessi Collegati", "Il tuo account social è stato abbinato alla tua cantina esistente con successo!", "success");
      setIsLinkingMode(false);
      setPendingCredential(null);
      setLinkPassword('');
    } catch (error) {
      showAppAlert("Collegamento Fallito", "La password inserita non è corretta. Riprova.", "error");
    } finally {
      if (Platform.OS === 'web') document.body.style.cursor = 'default';
      setLoading(false);
    }
  };

  // --- VERIFICA STATO UTENTE POST-LOGIN ---
  const checkUserComplianceAndProceed = async (firebaseUser, optionalName = '') => {
    try {
      const userRef = doc(db, 'users', firebaseUser.uid);
      const snap = await getDoc(userRef);

      const isSocialProvider = firebaseUser.providerData.some(
        p => p.providerId === 'google.com' || p.providerId === 'apple.com'
      );

      const hasPasswordProvider = firebaseUser.providerData.some(
        p => p.providerId === 'password'
      );

      if (snap.exists() && snap.data().termsAccepted && snap.data().isAdult) {
        if (isSocialProvider && !snap.data().hasSecurePassword && !hasPasswordProvider) {
          setIsSocialRegistration(true);
          setName(snap.data().name || firebaseUser.displayName || '');
          setIsAdultConfirmed(true);
          setIsTermsAccepted(true);
          setOnboardingUser(firebaseUser);
        } else {
          if (hasPasswordProvider && !snap.data().hasSecurePassword) {
            await updateDoc(userRef, { hasSecurePassword: true });
          }

          if (refreshWines) {
            await refreshWines();
          }
        }
      } else {
        setIsSocialRegistration(isSocialProvider && !hasPasswordProvider);
        setName(optionalName || firebaseUser.displayName || firebaseUser.email?.split('@')[0] || '');
        setIsAdultConfirmed(false);
        setIsTermsAccepted(false);
        setOnboardingUser(firebaseUser);
      }
    } catch (err) {
      handleAuthErrors(err, "Controllo Profilazione Post-Auth");
    }
  };

  // --- SALVATAGGIO ONBOARDING & INVIO EMAIL DI VERIFICA INIZIALE ---
  const saveOnboardingCompliance = async () => {
    if (!isAdultConfirmed || !isTermsAccepted || !onboardingUser) {
      showAppAlert("Consenso Richiesto", "Devi confermare la maggiore età e accettare i termini per sbloccare la tua cantina.", "error");
      return;
    }

    if (isSocialRegistration && socialSecurePassword.length < 6) {
      showAppAlert("Sicurezza Account", "Inserisci una password di almeno 6 caratteri per proteggere il tuo account da eliminazioni accidentali.", "error");
      return;
    }

    setLoading(true);
    try {
      const activeAuth = Platform.OS === 'web' ? getAuth() : auth;
      
      if (isSocialRegistration) {
        try {
          const credential = EmailAuthProvider.credential(onboardingUser.email, socialSecurePassword);
          await linkWithCredential(activeAuth.currentUser, credential);
        } catch (linkErr) {
          if (linkErr.code === 'auth/provider-already-linked') {
            await updatePassword(activeAuth.currentUser, socialSecurePassword);
          } else {
            throw linkErr;
          }
        }

        if (Platform.OS !== 'web' && isBiometricSupported && isBiometricRequested) {
          await saveCredentialsToVault(onboardingUser.email, socialSecurePassword);
        }
      }

      const userRef = doc(db, 'users', onboardingUser.uid);
      const userSnap = await getDoc(userRef);

      const profileData = {
        name: name.trim() || 'Utente',
        country: country,
        language: language,
        email: onboardingUser.email?.toLowerCase() || '',
        isAdult: true,
        termsAccepted: true,
        hasSecurePassword: true,
        isBiometricEnabled: isBiometricRequested
      };

      if (!userSnap.exists()) {
        profileData.createdAt = Date.now();
        profileData.energia = 5;
        profileData.isPremium = false;
        profileData.premiumType = 'none';
        profileData.lastEnergyReset = Date.now();
        await setDoc(userRef, profileData);
      } else {
        await updateDoc(userRef, profileData);
      }

      if (refreshWines) {
        await refreshWines();
      }

      const isPasswordProvider = onboardingUser.providerData.some(p => p.providerId === 'password');
      
      if (isPasswordProvider && !onboardingUser.emailVerified) {
        try {
          await sendEmailVerification(onboardingUser);
        } catch (mailErr) {
          console.warn("[VinoLogic] Errore invio verifica e-mail iniziale:", mailErr);
        }

        await signOut(activeAuth);
        setOnboardingUser(null);
        
        showAppAlert(
          t('login.alerts.registerSuccessTitle', language), 
          `${t('login.alerts.verifyEmailMsg', language)}\n\nAbbiamo inviato il link all'indirizzo:\n${onboardingUser.email}\n(Se non la trovi, controlla la cartella Spam o Posta Indesiderata).`, 
          'info'
        );

        setIsEmailSignupMode(false);
        setPassword('');
        return;
      }

      setOnboardingUser(null);
      setSocialSecurePassword('');
      setIsBiometricRequested(false);
      setIsSocialRegistration(false);

    } catch (err) {
      handleAuthErrors(err, "Scrittura Onboarding Legale e Password");
    } finally {
      setLoading(false);
    }
  };

  // --- AUTENTICAZIONE SOCIAL: GOOGLE CON DEBUGGER ESPLICITO ---
  const handleGoogleSignIn = async () => {
    Keyboard.dismiss();
    setLoading(true);
    try {
      if (Platform.OS === 'web') {
        const webAuth = getAuth();
        const provider = new GoogleAuthProvider();
        
        const mustForcePrompt = localStorage.getItem('vinologic_force_google_prompt');
        if (mustForcePrompt === 'true') {
          provider.setCustomParameters({ prompt: 'select_account' });
        }

        const result = await signInWithPopup(webAuth, provider);
        localStorage.removeItem('vinologic_force_google_prompt');
        
        await checkUserComplianceAndProceed(result.user);
      } else {
        await GoogleSignin.hasPlayServices({ showPlayServicesUpdateDialog: true });
        await GoogleSignin.signOut().catch(() => {});
        
        const response = await GoogleSignin.signIn();
        const idToken = response.data?.idToken || response.idToken;
        
        if (!idToken) {
          throw new Error(`ID_TOKEN_NULL -> Risposta nativa: ${JSON.stringify(response)}`);
        }
        
        const credential = GoogleAuthProvider.credential(idToken);
        const userCredential = await signInWithCredential(auth, credential);
        
        await checkUserComplianceAndProceed(userCredential.user);
      }
    } catch (error) {
      const activePackage = Constants.expoConfig?.android?.package || Constants.manifest?.android?.package || 'N/D';
      const rawErrorProps = JSON.stringify(error, Object.getOwnPropertyNames(error), 2);
      
      showAppAlert(
        "Ispezione Errore Google Sign-In",
        `Dati diagnostici runtime:\n\n• Code: ${error.code}\n• Message: ${error.message}\n• Package rilevato: ${activePackage}\n• Web Client ID: ${GOOGLE_WEB_CLIENT_ID}\n\nDettagli completi:\n${rawErrorProps}`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // --- AUTENTICAZIONE SOCIAL: APPLE ---
  const handleAppleSignIn = async () => {
    Keyboard.dismiss();
    showAppAlert(
      "Accesso con Apple",
      "Il login tramite account Apple sarà presto disponibile con il lancio ufficiale dell'applicazione nello store.\n\nNel frattempo, puoi effettuare l'accesso o registrarti con Google oppure inserendo la tua E-mail e Password.",
      "info"
    );
  };

  const saveCredentialsToVault = async (emailToSave, passwordToSave) => {
    if (Platform.OS === 'web') return;
    try {
      let accounts = [];
      const savedStr = await SecureStore.getItemAsync('savedAccounts');
      if (savedStr) accounts = JSON.parse(savedStr);

      accounts = accounts.filter(a => a.email.toLowerCase() !== emailToSave.toLowerCase());
      accounts.push({ email: emailToSave.toLowerCase(), password: passwordToSave });
      await SecureStore.setItemAsync('savedAccounts', JSON.stringify(accounts));
    } catch (error) {
      console.error("Errore cassaforte:", error);
    }
  };

  // --- LOGIN CON GESTIONE REINVIO VERIFICA ---
  const handleLogin = async () => {
    if (!email || !password) {
      return showAppAlert(
        t('login.alerts.missingCredentialsTitle', language), 
        t('login.alerts.missingCredentialsMsg', language), 
        'error'
      );
    }

    setLoading(true);
    try {
      const activeAuth = Platform.OS === 'web' ? getAuth() : auth;
      const userCredential = await signInWithEmailAndPassword(activeAuth, email.trim(), password);
      
      if (!userCredential.user.emailVerified) {
        showAppAlert(
          t('login.alerts.verifyEmailTitle', language),
          `${t('login.alerts.verifyEmailMsg', language)}\n\nNon hai ricevuto l'email o il link è scaduto?\nPremi "Reinvia Email" e controlla anche nello Spam.`,
          'confirm',
          async () => {
            try {
              await sendEmailVerification(userCredential.user);
              await signOut(activeAuth);
              showAppAlert(
                "Email Inviata", 
                `Abbiamo inviato un nuovo link di verifica a:\n${userCredential.user.email}\n\nControlla la posta in arrivo e la cartella Spam.`, 
                "success"
              );
            } catch (resendErr) {
              await signOut(activeAuth);
              if (resendErr.code === 'auth/too-many-requests') {
                showAppAlert("Attendi", "Abbiamo già inviato un'email di recente. Attendi qualche minuto prima di richiederne un'altra.", "info");
              } else {
                showAppAlert("Errore", "Impossibile reinviare l'email al momento. Riprova più tardi.", "error");
              }
            }
          },
          "Reinvia Email",
          async () => {
            await signOut(activeAuth);
          },
          "Chiudi"
        );

        setLoading(false);
        return;
      }

      await saveCredentialsToVault(email.trim(), password);
      await checkUserComplianceAndProceed(userCredential.user);
    } catch (error) {
      handleAuthErrors(error, 'Standard Email Password Login Flow');
    } finally {
      setLoading(false);
    }
  };

  const handleBiometricClick = async () => {
    if (Platform.OS === 'web') {
      return showAppAlert(t('login.alerts.missingCredentialsTitle', language), t('login.alerts.webBiometricMsg', language), 'info');
    }
    try {
      let accounts = [];
      const savedStr = await SecureStore.getItemAsync('savedAccounts');
      if (savedStr) accounts = JSON.parse(savedStr);

      if (accounts.length === 0) {
        return showAppAlert(t('login.alerts.biometricSetupTitle', language), t('login.alerts.biometricSetupMsg', language), 'info');
      }

      if (accounts.length === 1) {
        executeBiometricLogin(accounts[0].email, accounts[0].password);
      } else {
        setSavedAccountsList(accounts);
        setShowAccountSelector(true);
      }
    } catch (error) {
      showAppAlert(t('login.alerts.missingCredentialsTitle', language), t('login.alerts.vaultErrorMsg', language), 'error');
    }
  };

  const executeBiometricLogin = async (targetEmail, targetPassword) => {
    setShowAccountSelector(false); 
    try {
      const biometricAuth = await LocalAuthentication.authenticateAsync({
        promptMessage: `${t('login.biometricSelector.prompt', language)} ${targetEmail}`,
        disableDeviceFallback: false,
      });

      if (biometricAuth.success) {
        setLoading(true);
        const activeAuth = Platform.OS === 'web' ? getAuth() : auth;
        const userCredential = await signInWithEmailAndPassword(activeAuth, targetEmail, targetPassword);
        
        if (!userCredential.user.emailVerified) {
          await signOut(activeAuth);
          return showAppAlert(t('login.alerts.verifyEmailTitle', language), t('login.alerts.verifyEmailMsg', language), 'info');
        }

        await checkUserComplianceAndProceed(userCredential.user);
      }
    } catch (error) {
      showAppAlert(t('login.alerts.missingCredentialsTitle', language), t('login.alerts.biometricFailMsg', language), 'error');
      setLoading(false);
    }
  };

  const handleEmailRegistrationInit = async () => {
    if (!email || !password) {
      return showAppAlert(t('login.alerts.missingCredentialsTitle', language), t('login.alerts.missingFieldsMsg', language), 'error');
    }
    if (password.length < 6) {
      return showAppAlert(t('login.alerts.missingCredentialsTitle', language), t('login.alerts.shortPasswordMsg', language), 'error');
    }
    setLoading(true);
    try {
      const activeAuth = Platform.OS === 'web' ? getAuth() : auth;
      const userCredential = await createUserWithEmailAndPassword(activeAuth, email.trim(), password);
      await checkUserComplianceAndProceed(userCredential.user);
    } catch (error) {
      handleAuthErrors(error, 'Email Registration Initialization');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!email || !email.trim()) {
      return showAppAlert(
        t('login.alerts.missingCredentialsTitle', language), 
        "Inserisci il tuo indirizzo email nel campo sopra per ricevere il link di ripristino password.", 
        'info'
      );
    }

    try {
      const activeAuth = Platform.OS === 'web' ? getAuth() : auth;
      await sendPasswordResetEmail(activeAuth, email.trim());
      showAppAlert(
        t('login.alerts.resetEmailSentTitle', language), 
        `${t('login.alerts.resetEmailSentMsg', language)}\n\nInviata a: ${email.trim()}\n(Controlla anche la cartella Spam).`, 
        'success'
      );
    } catch (error) {
      handleAuthErrors(error, 'Reset Password Flow');
    }
  };

  const renderFlagGraphic = (langCode) => {
    if (Platform.OS === 'web') {
      if (langCode === 'it') {
        return (
          <svg width="16" height="12" viewBox="0 0 3 2" style={{ marginRight: 6, borderRadius: 1 }}>
            <rect width="1" height="2" fill="#009246"/>
            <rect x="1" width="1" height="2" fill="#fff"/>
            <rect x="2" width="1" height="2" fill="#ce2b37"/>
          </svg>
        );
      }
      if (langCode === 'en') {
        return (
          <svg width="16" height="10" viewBox="0 0 50 30" style={{ marginRight: 6, borderRadius: 1 }}>
            <rect width="50" height="30" fill="#012169"/>
            <clipPath id="s"><path d="M0,0 v30 h50 v-30 z"/></clipPath>
            <path d="M0,0 L50,30 M50,0 L0,30" stroke="#fff" strokeWidth="6" clipPath="url(#s)"/>
            <path d="M0,0 L50,30 M50,0 L0,30" stroke="#C8102E" strokeWidth="4" clipPath="url(#s)"/>
            <path d="M0,15 H50 M25,0 V30" stroke="#fff" strokeWidth="10"/>
            <path d="M0,15 H50 M25,0 V30" stroke="#C8102E" strokeWidth="6"/>
          </svg>
        );
      }
      if (langCode === 'fr') {
        return (
          <svg width="16" height="12" viewBox="0 0 3 2" style={{ marginRight: 6, borderRadius: 1 }}>
            <rect width="1" height="2" fill="#0055A4"/>
            <rect x="1" width="1" height="2" fill="#fff"/>
            <rect x="2" width="1" height="2" fill="#EF4135"/>
          </svg>
        );
      }
    } else {
      if (langCode === 'it') return "🇮🇹 ";
      if (langCode === 'en') return "🇬🇧 ";
      if (langCode === 'fr') return "🇫🇷 ";
    }
    return null;
  };

  return (
    <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
        
        <View style={styles.formContainer}>
          
          <View style={styles.globalLangSelectorRow}>
            <TouchableOpacity 
              style={[styles.miniLangTab, language === 'it' && styles.miniLangTabActive]} 
              onPress={() => { setLanguage('it'); setCountry('Italia'); }}
            >
              <View style={styles.miniTabContent}>
                {renderFlagGraphic('it')}
                <Text style={[styles.miniLangText, language === 'it' && styles.miniLangTabActive && styles.miniLangTextActive]}>ITA</Text>
              </View>
            </TouchableOpacity>
            <TouchableOpacity 
              style={[styles.miniLangTab, language === 'en' && styles.miniLangTabActive]} 
              onPress={() => { setLanguage('en'); setCountry('International'); }}
            >
              <View style={styles.miniTabContent}>
                {renderFlagGraphic('en')}
                <Text style={[styles.miniLangText, language === 'en' && styles.miniLangTabActive && styles.miniLangTextActive]}>ENG</Text>
              </View>
            </TouchableOpacity>
            <TouchableOpacity 
              style={[styles.miniLangTab, language === 'fr' && styles.miniLangTabActive]} 
              onPress={() => { setLanguage('fr'); setCountry('France'); }}
            >
              <View style={styles.miniTabContent}>
                {renderFlagGraphic('fr')}
                <Text style={[styles.miniLangText, language === 'fr' && styles.miniLangTabActive && styles.miniLangTextActive]}>FRA</Text>
              </View>
            </TouchableOpacity>
          </View>

          <View style={styles.logoContainer}>
            <Text style={{fontSize: 50, marginBottom: 10}}>🍷</Text>
            <Text style={styles.title}>VinoLogic</Text>
            <Text style={styles.subtitle}>{t('login.subtitle', language)}</Text>
          </View>

          <View style={styles.socialButtonsGroup}>
            {Platform.OS !== 'web' ? (
              <GoogleSigninButton
                style={styles.googleButtonNative}
                size={GoogleSigninButton.Size.Wide}
                color={GoogleSigninButton.Color.Light}
                onPress={handleGoogleSignIn}
                disabled={loading}
              />
            ) : (
              <TouchableOpacity style={styles.googleButtonWeb} onPress={handleGoogleSignIn} activeOpacity={0.8}>
                <View style={styles.googleIconWrapperWeb}>
                  <svg width="18" height="18" viewBox="0 0 18 18">
                    <path fill="#4285F4" d="M17.64 9.2c0-.63-.06-1.25-.16-1.84H9v3.47h4.84c-.21 1.12-.84 2.07-1.79 2.7l2.78 2.16c1.63-1.51 2.57-3.73 2.57-6.39z"/>
                    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.78-2.16c-.77.52-1.75.83-2.93.83-2.25 0-4.16-1.52-4.84-3.57H1.51v2.23C3 16.12 5.81 18 9 18z"/>
                    <path fill="#FBBC05" d="M4.16 10.92c-.17-.52-.27-1.07-.27-1.64s.1-1.12.27-1.64V5.41H1.51C.93 6.56.61 7.87.61 9.25s.32 2.69.9 3.84l2.65-2.17z"/>
                    <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46 1.02 11.42.61 9.25.61c-3.19 0-6 1.88-7.49 4.8l2.65 2.17c.68-2.05 2.59-3.57 4.84-3.57z"/>
                  </svg>
                </View>
                <Text style={styles.googleButtonLabelWeb}>
                  {t('login.buttons.continueWithGoogle', language) || "Continua con Google"}
                </Text>
              </TouchableOpacity>
            )}

            {Platform.OS === 'ios' && (
              <AppleAuthentication.AppleAuthenticationButton
                buttonType={AppleAuthentication.AppleAuthenticationButtonType.CONTINUE}
                buttonStyle={AppleAuthentication.AppleAuthenticationButtonStyle.BLACK}
                cornerRadius={12}
                style={styles.appleButtonNative}
                onPress={handleAppleSignIn}
              />
            )}
            
            {Platform.OS === 'web' && (
              <TouchableOpacity style={styles.appleButtonWeb} onPress={handleAppleSignIn} activeOpacity={0.8}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="white" style={{ marginRight: 10, marginBottom: 2 }}>
                  <path d="M11.182.008C11.148.004 11.115 0 11.08 0c-1.073 0-2.18.675-2.825 1.455-.636.764-1.125 1.874-.954 2.946.035.004.07.007.103.007.994 0 2.127-.643 2.76-1.442.65-.82 1.134-1.936.953-2.946-.012-.007-.024-.01-.035-.012zM13.784 12.35c-.11-.064-2.17-1.282-2.152-3.81.022-3.023 2.653-4.027 2.682-4.04-.028-.066-.42-1.436-1.378-2.827-.828-1.213-1.693-2.42-3.027-2.42-1.314 0-1.724.787-3.23.787-1.503 0-1.961-.767-3.21-.767-1.296 0-2.28 1.159-3.118 2.37C.1 4.542-1.205 9.117.554 12.18c.871 1.517 2.425 2.476 4.103 2.502 1.272.022 2.482-.857 3.265-.857.772 0 2.228.98 3.738.824.63-.024 2.413-.255 3.56-1.942-.092-.055-.42-.647-.435-1.357z"/>
                </svg>
                <Text style={styles.appleButtonLabelWeb}>
                  {t('login.buttons.continueWithApple', language) || "Continua con Apple"}
                </Text>
              </TouchableOpacity>
            )}
          </View>

          <View style={styles.dividerRow}>
            <View style={styles.dividerLine} />
            <Text style={styles.dividerText}>{t('login.buttons.orWithEmail', language) || "oppure con e-mail"}</Text>
            <View style={styles.dividerLine} />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>{t('login.fields.emailLabel', language)}</Text>
            <TextInput
              style={styles.input}
              placeholder={t('login.fields.emailPlaceholder', language)}
              value={email}
              onChangeText={setEmail}
              keyboardType="email-address"
              autoCapitalize="none"
              placeholderTextColor="#94a3b8"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>{t('login.fields.passwordLabel', language)}</Text>
            <View style={styles.passwordContainer}>
              <TextInput
                style={styles.passwordInput}
                placeholder={t('login.fields.passwordPlaceholderLogin', language)}
                value={password}
                onChangeText={setPassword}
                secureTextEntry={!showPassword}
                placeholderTextColor="#94a3b8"
              />
              <TouchableOpacity onPress={() => setShowPassword(!showPassword)} style={styles.eyeButton}>
                {showPassword ? <EyeOff color="#64748b" size={20} /> : <Eye color="#64748b" size={20} />}
              </TouchableOpacity>
            </View>
          </View>

          {loading ? (
            <ActivityIndicator size="large" color="#e11d48" style={{ marginTop: 20 }} />
          ) : (
            <View style={styles.buttonsContainer}>
              <TouchableOpacity 
                style={styles.primaryButton} 
                onPress={isEmailSignupMode ? handleEmailRegistrationInit : handleLogin}
              >
                <Text style={styles.primaryButtonText}>
                  {isEmailSignupMode ? (t('login.buttons.registerAction', language) || "Registrati") : (t('login.buttons.loginAction', language) || "Accedi")}
                </Text>
              </TouchableOpacity>

              {Platform.OS !== 'web' && isBiometricSupported && (
                <TouchableOpacity style={styles.biometricButton} onPress={handleBiometricClick}>
                  <Fingerprint color="#e11d48" size={28} />
                </TouchableOpacity>
              )}
            </View>
          )}

          <View style={styles.switchModeContainer}>
            <TouchableOpacity onPress={() => setIsEmailSignupMode(!isEmailSignupMode)}>
              <Text style={{ color: '#64748b', fontSize: 14, textDecorationLine: 'underline', fontWeight: '500' }}>
                {isEmailSignupMode ? (t('login.tabs.login', language) || "Accedi") : (t('login.tabs.register', language) || "Registrati")}
              </Text>
            </TouchableOpacity>

            {!isEmailSignupMode && (
              <TouchableOpacity onPress={handleResetPassword}>
                <Text style={styles.forgotPasswordText}>{t('login.buttons.forgotPassword', language)}</Text>
              </TouchableOpacity>
            )}
          </View>

        </View>
      </ScrollView>

      {/* ======================================================== */}
      {/* 🌟 ONBOARDING & COMPLIANCE MODAL GDPR */}
      {/* ======================================================== */}
      <Modal transparent={false} visible={onboardingUser !== null} animationType="fade">
        <View style={styles.onboardingOverlay}>
          <ScrollView contentContainerStyle={styles.onboardingScroll} showsVerticalScrollIndicator={false}>
            <View style={styles.onboardingCard}>
              <Text style={{ fontSize: 44, textAlign: 'center', marginBottom: 10 }}>🍷</Text>
              <Text style={styles.onboardingTitle}>Configura la tua Cantina</Text>
              <Text style={styles.onboardingSubtitle}>Completa gli ultimi dettagli obbligatori per attivare VinoLogic ed entrare nel tuo spazio di gestione virtuale.</Text>
              
              <View style={styles.inputGroup}>
                <Text style={styles.label}>{t('login.registration.nameLabel', language) || "Il tuo Nome / Nome Cantina"}</Text>
                <View style={styles.inputWrapper}>
                  <User color="#94a3b8" size={20} style={styles.inputIcon} />
                  <TextInput
                    style={styles.inputWithIcon}
                    placeholder={t('login.registration.namePlaceholder', language)}
                    value={name}
                    onChangeText={setName}
                    placeholderTextColor="#94a3b8"
                  />
                </View>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>{t('login.registration.langCountryLabel', language) || "Lingua Preferita"}</Text>
                <View style={styles.languageButtons}>
                  <TouchableOpacity style={[styles.langBtn, language === 'it' && styles.langBtnActive]} onPress={() => { setLanguage('it'); setCountry('Italia'); }}>
                    <View style={styles.miniTabContent}>
                      {renderFlagGraphic('it')}
                      <Text style={[styles.langText, language === 'it' && styles.langTextActive]}>Italiano</Text>
                    </View>
                  </TouchableOpacity>
                  <TouchableOpacity style={[styles.langBtn, language === 'en' && styles.langBtnActive]} onPress={() => { setLanguage('en'); setCountry('International'); }}>
                    <View style={styles.miniTabContent}>
                      {renderFlagGraphic('en')}
                      <Text style={[styles.langText, language === 'en' && styles.langTextActive]}>English</Text>
                    </View>
                  </TouchableOpacity>
                  <TouchableOpacity style={[styles.langBtn, language === 'fr' && styles.langBtnActive]} onPress={() => { setLanguage('fr'); setCountry('France'); }}>
                    <View style={styles.miniTabContent}>
                      {renderFlagGraphic('fr')}
                      <Text style={[styles.langText, language === 'fr' && styles.langTextActive]}>Français</Text>
                    </View>
                  </TouchableOpacity>
                </View>
              </View>

              {/* SEZIONE DINAMICA DI SICUREZZA PER LOGIN SOCIAL (GOOGLE / APPLE) */}
              {isSocialRegistration && (
                <View style={[styles.securitySection, { marginVertical: 14, padding: 16, backgroundColor: '#fdf2f2', borderRadius: 16, borderWidth: 1, borderColor: '#fde2e2' }]}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                    <Lock color="#e11d48" size={18} style={{ marginRight: 6 }} />
                    <Text style={{ fontSize: 14, fontWeight: '700', color: '#991b1b' }}>Protezione Account Richiesta</Text>
                  </View>
                  <Text style={{ fontSize: 12, color: '#7f1d1d', marginBottom: 12, lineHeight: 16 }}>
                    Per evitare cancellazioni accidentali della cantina tramite click automatici del browser, crea una password di sicurezza VinoLogic dedicata.
                  </Text>
                  
                  <View style={styles.passwordContainer}>
                    <TextInput
                      style={styles.passwordInput}
                      placeholder="Imposta Password Sicurezza"
                      value={socialSecurePassword}
                      onChangeText={setSocialSecurePassword}
                      secureTextEntry={!showSocialPassword}
                      placeholderTextColor="#94a3b8"
                    />
                    <TouchableOpacity onPress={() => setShowSocialPassword(!showSocialPassword)} style={styles.eyeButton}>
                      {showSocialPassword ? <EyeOff color="#64748b" size={20} /> : <Eye color="#64748b" size={20} />}
                    </TouchableOpacity>
                  </View>

                  {Platform.OS !== 'web' && isBiometricSupported && (
                    <TouchableOpacity 
                      style={{ flexDirection: 'row', alignItems: 'center', marginTop: 14, gap: 10 }}
                      onPress={() => setIsBiometricRequested(!isBiometricRequested)}
                      activeOpacity={0.7}
                    >
                      {isBiometricRequested ? <CheckSquare color="#e11d48" size={22} /> : <Square color="#94a3b8" size={22} />}
                      <Text style={{ fontSize: 13, color: '#475569', fontWeight: '600' }}>Attiva anche autenticazione biometrica</Text>
                    </TouchableOpacity>
                  )}
                </View>
              )}

              <View style={styles.complianceContainer}>
                <View style={styles.ageVerificationContainer}>
                  <TouchableOpacity style={styles.checkboxRow} onPress={() => setIsAdultConfirmed(!isAdultConfirmed)} activeOpacity={0.7}>
                    {isAdultConfirmed ? <CheckSquare color="#e11d48" size={22} /> : <Square color="#94a3b8" size={22} />}
                    <Text style={styles.checkboxText}>{t('login.compliance.adultDeclaration', language)}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={showAgeInfo} style={styles.infoIconBtn}><Info color="#64748b" size={20} /></TouchableOpacity>
                </View>

                <TouchableOpacity style={styles.checkboxRowGeneric} onPress={() => setIsTermsAccepted(!isTermsAccepted)} activeOpacity={0.7}>
                  {isTermsAccepted ? <CheckSquare color="#e11d48" size={22} /> : <Square color="#94a3b8" size={22} />}
                  <Text style={styles.legalLabelText}>
                    {t('login.compliance.termsReadAndAccept', language)}{' '}
                    <Text style={styles.legalLink} onPress={() => openLegalDocument('terms')}>{t('login.compliance.termsOfService', language)}</Text>
                    {' '}{t('login.compliance.andThe') || "e della"}{' '}
                    <Text style={styles.legalLink} onPress={() => openLegalDocument('privacy')}>{t('login.compliance.privacyPolicy', language)}</Text>.
                  </Text>
                </TouchableOpacity>
              </View>

              {loading ? (
                <ActivityIndicator size="large" color="#e11d48" style={{ marginTop: 10 }} />
              ) : (
                <TouchableOpacity 
                  style={[styles.primaryButton, (!isAdultConfirmed || !isTermsAccepted || !name.trim() || (isSocialRegistration && socialSecurePassword.length < 6)) && { backgroundColor: '#cbd5e1', opacity: 0.7 }]} 
                  onPress={saveOnboardingCompliance}
                  disabled={!isAdultConfirmed || !isTermsAccepted || !name.trim() || (isSocialRegistration && socialSecurePassword.length < 6)}
                >
                  <Text style={styles.primaryButtonText}>Attiva la tua Cantina 🍷</Text>
                </TouchableOpacity>
              )}
            </View>
          </ScrollView>
        </View>
      </Modal>

      {/* MODAL LINKING ACCOUNT */}
      <Modal transparent={true} visible={isLinkingMode} animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalBox}>
            <View style={styles.modalHeader}>
              <ShieldAlert color="#e11d48" size={28} />
              <Text style={styles.modalTitle}>Cantina Esistente</Text>
            </View>
            <Text style={styles.modalMessage}>
              L'indirizzo e-mail del tuo account social coincide con una cantina VinoLogic esistente. Inserisci la tua password tradizionale per unire in sicurezza gli accessi.
            </Text>
            <View style={[styles.passwordContainer, { marginBottom: 20, backgroundColor: '#f8fafc' }]}>
              <KeyRound color="#94a3b8" size={20} style={{ marginLeft: 16 }} />
              <TextInput
                style={styles.passwordInput}
                placeholder="Vecchia password"
                placeholderTextColor="#94a3b8"
                secureTextEntry
                value={linkPassword}
                onChangeText={setLinkPassword}
              />
            </View>
            <View style={styles.modalButtons}>
              <TouchableOpacity style={[styles.modalConfirmBtn, { backgroundColor: '#f1f5f9' }]} onPress={() => { setIsLinkingMode(false); setPendingCredential(null); setLinkPassword(''); }}>
                <Text style={[styles.modalConfirmText, { color: '#475569' }]}>{t('global.cancel', language) || "Annulla"}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.modalConfirmBtn, { backgroundColor: '#e11d48' }]} onPress={executeAccountLinking}>
                <Text style={styles.modalConfirmText}>Collega</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* POPUP MODALE DI SISTEMA COMPLETO DI AZIONI DI CONFERMA / ANNULLA */}
      <Modal transparent={true} visible={customAlert.visible} animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalBox}>
            <View style={styles.modalHeader}>
              {customAlert.type === 'error' ? (
                <AlertCircle color="#ef4444" size={28} />
              ) : customAlert.type === 'success' ? (
                <CheckCircle color="#10b981" size={28} />
              ) : customAlert.type === 'confirm' ? (
                <MailCheck color="#e11d48" size={28} />
              ) : (
                <Info color="#3b82f6" size={28} />
              )}
              <Text style={styles.modalTitle}>{customAlert.title}</Text>
            </View>
            <Text style={styles.modalMessage}>{customAlert.message}</Text>
            <View style={styles.modalButtons}>
              {customAlert.type === 'confirm' && (
                <TouchableOpacity 
                  style={[styles.modalConfirmBtn, { backgroundColor: '#f1f5f9' }]} 
                  onPress={() => {
                    if (customAlert.onCancel) customAlert.onCancel();
                    closeAppAlert();
                  }}
                >
                  <Text style={[styles.modalConfirmText, { color: '#475569' }]}>
                    {customAlert.cancelText || t('login.buttons.cancel', language)}
                  </Text>
                </TouchableOpacity>
              )}
              
              <TouchableOpacity 
                style={[
                  styles.modalConfirmBtn, 
                  customAlert.type === 'error' ? { backgroundColor: '#e11d48' } : 
                  customAlert.type === 'success' ? { backgroundColor: '#10b981' } : 
                  customAlert.type === 'confirm' ? { backgroundColor: '#e11d48' } :
                  { backgroundColor: '#0f172a' }
                ]} 
                onPress={() => {
                  if (customAlert.onConfirm) {
                    customAlert.onConfirm();
                  }
                  closeAppAlert();
                }}
              >
                <Text style={styles.modalConfirmText}>
                  {customAlert.type === 'confirm' ? (customAlert.confirmText || "OK") : t('login.buttons.dismiss', language)}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* SELECTOR ACCOUNT BIOMETRICO */}
      <Modal transparent={true} visible={showAccountSelector} animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.selectorBox}>
            <View style={styles.selectorHeader}>
              <Text style={styles.selectorTitle}>{t('login.biometricSelector.title', language)}</Text>
              <Text style={styles.selectorSubtitle}>{t('login.biometricSelector.subtitle', language)}</Text>
            </View>
            <ScrollView style={{maxHeight: 250}}>
              {savedAccountsList.map((acc, index) => (
                <TouchableOpacity key={index} style={styles.accountRow} onPress={() => executeBiometricLogin(acc.email, acc.password)}>
                  <View style={styles.accountIconBg}>
                    <User color="#0f172a" size={20} />
                  </View>
                  <Text style={styles.accountEmailText} numberOfLines={1}>{acc.email}</Text>
                  <ChevronRight color="#cbd5e1" size={20} />
                </TouchableOpacity>
              ))}
            </ScrollView>
            <TouchableOpacity style={styles.selectorCancelBtn} onPress={() => setShowAccountSelector(false)}>
              <Text style={styles.selectorCancelText}>{t('login.buttons.cancel', language)}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8fafc' },
  scrollContent: { flexGrow: 1, justifyContent: 'center' },
  formContainer: { 
    padding: 24, backgroundColor: 'white', margin: 20, borderRadius: 32, marginTop: 30, marginBottom: 40,
    ...Platform.select({
      ios: { shadowColor: 'rgba(0, 0, 0, 0.05)', shadowOffset: { width: 0, height: 10 }, shadowOpacity: 1, shadowRadius: 25 },
      android: { elevation: 5 },
      web: { boxShadow: '0px 10px 25px rgba(0, 0, 0, 0.05)' }
    })
  },
  globalLangSelectorRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    marginBottom: 16
  },
  miniLangTab: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    backgroundColor: '#f8fafc'
  },
  miniLangTabActive: {
    backgroundColor: '#0f172a',
    borderColor: '#0f172a'
  },
  miniLangText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#64748b'
  },
  miniLangTextActive: {
    color: '#ffffff'
  },
  miniTabContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center'
  },
  logoContainer: { alignItems: 'center', marginBottom: 32 },
  title: { fontSize: 32, fontWeight: 'bold', color: '#0f172a' }, 
  subtitle: { fontSize: 15, color: '#64748b', marginTop: 4, textAlign: 'center' },
  
  socialButtonsGroup: { gap: 12, marginBottom: 12, width: '100%' },
  googleButtonNative: { width: '100%', height: 48, marginBottom: 4 },
  appleButtonNative: { width: '100%', height: 44, marginBottom: 4 },
  googleButtonWeb: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#ffffff', height: 40, borderRadius: 4, borderWidth: 1, borderColor: '#747775', paddingHorizontal: 12,
    ...Platform.select({ web: { boxShadow: '0px 1px 3px rgba(0,0,0,0.08)' } })
  },
  googleIconWrapperWeb: { marginRight: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  googleButtonLabelWeb: { color: '#1f1f1f', fontWeight: '500', fontSize: 14, fontFamily: 'Roboto, sans-serif' },
  appleButtonWeb: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#000000', height: 40, borderRadius: 4, borderWidth: 1, borderColor: '#000000', paddingHorizontal: 12 },
  appleButtonLabelWeb: { color: '#ffffff', fontWeight: '500', fontSize: 14, fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' },

  dividerRow: { flexDirection: 'row', alignItems: 'center', marginVertical: 16 },
  dividerLine: { flex: 1, height: 1, backgroundColor: '#e2e8f0' },
  dividerText: { color: '#94a3b8', paddingHorizontal: 12, fontSize: 12, textTransform: 'uppercase', fontWeight: '600' },

  inputGroup: { marginBottom: 16 },
  label: { fontSize: 14, fontWeight: '600', color: '#475569', marginBottom: 8, marginLeft: 4 },
  input: { backgroundColor: '#f8fafc', padding: 16, borderRadius: 16, fontSize: 16, color: '#0f172a', borderWidth: 1, borderColor: '#e2e8f0' },
  inputWrapper: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f8fafc', borderRadius: 16, borderWidth: 1, borderColor: '#e2e8f0', paddingHorizontal: 16 },
  inputIcon: { marginRight: 8 },
  inputWithIcon: { flex: 1, paddingVertical: 16, fontSize: 16, color: '#0f172a' },
  languageButtons: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  langBtn: { flex: 1, minWidth: 85, paddingVertical: 12, borderRadius: 12, borderWidth: 1, borderColor: '#e2e8f0', alignItems: 'center', backgroundColor: '#f8fafc' },
  langBtnActive: { backgroundColor: '#0f172a', borderColor: '#0f172a' },
  langText: { color: '#64748b', fontWeight: '600', fontSize: 13 },
  langTextActive: { color: 'white' },

  passwordContainer: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f8fafc', borderRadius: 16, borderWidth: 1, borderColor: '#e2e8f0' },
  passwordInput: { flex: 1, padding: 16, fontSize: 16, color: '#0f172a' },
  eyeButton: { padding: 16 },
  
  complianceContainer: { marginTop: 4, marginBottom: 16 },
  ageVerificationContainer: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, backgroundColor: '#f8fafc', padding: 12, borderRadius: 12, borderWidth: 1, borderColor: '#e2e8f0' },
  checkboxRow: { flexDirection: 'row', alignItems: 'center', flex: 1 },
  checkboxText: { marginLeft: 10, fontSize: 14, color: '#0f172a', fontWeight: '600' },
  infoIconBtn: { padding: 4, marginLeft: 10 },
  checkboxRowGeneric: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f8fafc', padding: 12, borderRadius: 12, borderWidth: 1, borderColor: '#e2e8f0' },
  legalLabelText: { marginLeft: 10, fontSize: 13, color: '#475569', flex: 1, lineHeight: 20 },
  legalLink: { color: '#e11d48', fontWeight: 'bold' },
  
  buttonsContainer: { flexDirection: 'row', gap: 12, marginTop: 12 },
  primaryButton: { flex: 1, backgroundColor: '#e11d48', padding: 16, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  primaryButtonText: { color: 'white', fontSize: 16, fontWeight: 'bold' },
  biometricButton: { backgroundColor: '#fff1f2', paddingHorizontal: 20, borderRadius: 16, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: '#ffe4e6' },
  forgotPasswordText: { color: '#64748b', fontSize: 14, textDecorationLine: 'underline' },
  switchModeContainer: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, paddingHorizontal: 4 },
  
  modalOverlay: { flex: 1, backgroundColor: 'rgba(15, 23, 42, 0.6)', justifyContent: 'center', alignItems: 'center', padding: 20 },
  modalBox: { backgroundColor: 'white', borderRadius: 24, padding: 24, width: '100%', maxWidth: 400, elevation: 10 },
  modalHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 16 },
  modalTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a', marginLeft: 12, flex: 1 },
  modalMessage: { fontSize: 16, color: '#475569', lineHeight: 24, marginBottom: 24 },
  modalButtons: { flexDirection: 'row', justifyContent: 'flex-end', gap: 12, marginTop: 10 },
  modalConfirmBtn: { paddingVertical: 12, paddingHorizontal: 20, borderRadius: 12, minWidth: 100, alignItems: 'center' },
  modalConfirmText: { color: 'white', fontWeight: 'bold', fontSize: 16 },
  selectorBox: { backgroundColor: 'white', borderRadius: 24, padding: 20, width: '100%', maxWidth: 400, elevation: 10 },
  selectorHeader: { marginBottom: 16, alignItems: 'center' },
  selectorTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a', marginBottom: 4 },
  selectorSubtitle: { fontSize: 14, color: '#64748b' },
  accountRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f8fafc', padding: 16, borderRadius: 16, marginBottom: 12, borderWidth: 1, borderColor: '#e2e8f0' },
  accountIconBg: { backgroundColor: '#e2e8f0', padding: 10, borderRadius: 12, marginRight: 12 },
  accountEmailText: { flex: 1, fontSize: 16, fontWeight: '600', color: '#0f172a' },
  selectorCancelBtn: { marginTop: 12, paddingVertical: 14, alignItems: 'center', backgroundColor: '#f1f5f9', borderRadius: 16 },
  selectorCancelText: { color: '#475569', fontWeight: 'bold', fontSize: 16 },

  onboardingOverlay: { flex: 1, backgroundColor: '#f8fafc', justifyContent: 'center', padding: 16 },
  onboardingScroll: { flexGrow: 1, justifyContent: 'center', paddingVertical: 20 },
  onboardingCard: {
    padding: 24, backgroundColor: 'white', borderRadius: 24, width: '100%', maxWidth: 440, alignSelf: 'center',
    ...Platform.select({ web: { boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.08)' } })
  },
  onboardingTitle: { fontSize: 26, fontWeight: 'bold', color: '#0f172a', textAlign: 'center', marginBottom: 8 },
  onboardingSubtitle: { fontSize: 14, color: '#64748b', textAlign: 'center', marginBottom: 24, lineHeight: 20 }
});